import { useEffect, useRef, useState } from 'react'
import {
    PALETTE_MIN_QUERY_LENGTH,
    type PaletteGroup,
    type PaletteItem,
    type PaletteTarget,
} from '../components/SearchPalette'
import { filterPaletteItems } from '../utils/paletteMatch'

/** Pages and actions the browser filters itself. */
export interface LocalPaletteSource {
    id: string
    label: string
    items: PaletteItem[]
}

/** Entities searched by calling the app's API. */
export interface RemotePaletteSource {
    id: string
    label: string
    /** Returns the first few matches and the total; must honour `signal` where the client supports it */
    search: (term: string, signal: AbortSignal) => Promise<{ items: PaletteItem[]; total: number }>
    /** Where the group header leads, typically the list page filtered by the term */
    viewAll?: (term: string) => PaletteTarget
}

export interface PaletteSearchOptions {
    localSources: LocalPaletteSource[]
    remoteSources: RemotePaletteSource[]
    /**
     * Results belong to this scope, such as the active organization. Changing it aborts the running search and
     * discards results gathered under the old scope.
     */
    scope?: string
    debounceMs?: number
    minQueryLength?: number
}

interface SearchResult {
    term: string
    scope?: string
    groups: PaletteGroup[]
}

/**
 * Searches local and remote palette sources for a query. Remote searches wait for typing to pause, run in parallel,
 * and drop sources that fail; a newer term aborts the older searches. Every group appears together once all
 * sources have answered, local groups first.
 */
export function usePaletteSearch(
    query: string,
    {
        localSources,
        remoteSources,
        scope,
        debounceMs = 300,
        minQueryLength = PALETTE_MIN_QUERY_LENGTH,
    }: PaletteSearchOptions
): { groups: PaletteGroup[]; loading: boolean } {
    const term = query.trim()
    const [debouncedTerm, setDebouncedTerm] = useState(term)
    const [result, setResult] = useState<SearchResult>({ term: '', groups: [] })
    const sourcesRef = useRef({ localSources, remoteSources })
    // Sources are often rebuilt on every render; only a change in which sources exist should start a new search.
    const sourceKey = [
        ...localSources.map((source) => `${source.id}:${source.items.map((item) => item.id).join(',')}`),
        ...remoteSources.map((source) => source.id),
    ].join('\n')

    useEffect(() => {
        sourcesRef.current = { localSources, remoteSources }
    })

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedTerm(term), debounceMs)
        return () => clearTimeout(timeout)
    }, [term, debounceMs])

    useEffect(() => {
        if (debouncedTerm.length < minQueryLength) {
            setResult({ term: debouncedTerm, scope, groups: [] })
            return
        }
        const controller = new AbortController()
        const { localSources, remoteSources } = sourcesRef.current

        const search = async () => {
            const settled = await Promise.allSettled(
                remoteSources.map((source) => source.search(debouncedTerm, controller.signal))
            )
            if (controller.signal.aborted) return

            const localGroups: PaletteGroup[] = localSources.map((source) => {
                const items = filterPaletteItems(source.items, debouncedTerm)
                return { id: source.id, label: source.label, items, total: items.length }
            })
            const remoteGroups: PaletteGroup[] = remoteSources.flatMap((source, index) => {
                const outcome = settled[index]
                if (outcome.status !== 'fulfilled') return []
                return [
                    {
                        id: source.id,
                        label: source.label,
                        items: outcome.value.items,
                        total: outcome.value.total,
                        viewAll: source.viewAll?.(debouncedTerm),
                    },
                ]
            })
            setResult({
                term: debouncedTerm,
                scope,
                groups: [...localGroups, ...remoteGroups].filter((group) => group.items.length > 0),
            })
        }

        void search()
        return () => controller.abort()
    }, [debouncedTerm, sourceKey, scope, minQueryLength])

    if (term.length < minQueryLength) return { groups: [], loading: false }
    const current = result.term === term && result.scope === scope
    return { groups: current ? result.groups : [], loading: !current }
}
